import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Users, Clock, CheckCircle, Plus, AlertTriangle, UserCheck } from "lucide-react";

const STATUS_FLOW = {
  waiting: { next: "in_consultation", label: "Check In", color: "bg-blue-600 hover:bg-blue-700" },
  in_consultation: { next: "completed", label: "Complete", color: "bg-emerald-600 hover:bg-emerald-700" },
};
const STATUS_STYLES = {
  waiting: "bg-amber-100 text-amber-800",
  in_consultation: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function ReceptionDashboard() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [todayAppts, setTodayAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ patient_id: "", doctor_id: "", visit_date: new Date().toISOString().slice(0, 16), complaint: "", is_emergency: false, visit_type: "OPD" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
    Promise.all([api.get("/patients"), api.get("/doctors")]).then(([p, d]) => {
      setPatients(p.data); setDoctors(d.data);
    });
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/appointments/today");
      setTodayAppts(data.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}/status`, { status });
      fetchData();
      toast({ title: `Status: ${status.replace("_", " ")}` });
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const handleBook = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/appointments", form);
      toast({ title: "Appointment booked" });
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const waiting = todayAppts.filter(a => a.status === "waiting");
  const inConsultation = todayAppts.filter(a => a.status === "in_consultation");
  const completed = todayAppts.filter(a => a.status === "completed");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Reception Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={() => setShowModal(true)} data-testid="book-appt-btn">
          <Plus size={16} /> Book Appointment
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Waiting", value: waiting.length, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "In Consultation", value: inConsultation.length, icon: UserCheck, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Completed Today", value: completed.length, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-5 flex items-center gap-3`} data-testid={`reception-stat-${label.toLowerCase().replace(" ", "-")}`}>
            <Icon size={24} className={color} />
            <div>
              <p className={`text-3xl font-bold font-sans ${color}`}>{value}</p>
              <p className="text-xs text-slate-600 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Token Queue */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Calendar size={16} className="text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-900 font-sans">Today's Token Queue ({todayAppts.length} appointments)</h2>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400">{t("loading")}</div>
        ) : todayAppts.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <Calendar size={32} className="mx-auto mb-2 opacity-30" />
            <p>No appointments today</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {todayAppts.map(appt => (
              <div key={appt.id} className={`flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors ${appt.is_emergency ? "border-l-4 border-rose-400 bg-rose-50/20" : ""}`} data-testid={`reception-appt-${appt.id}`}>
                {/* Token */}
                <div className="flex-shrink-0 w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-200">
                  <span className="text-sm font-black text-emerald-700">#{appt.token_number}</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 text-sm">{appt.patient_name}</p>
                    {appt.is_emergency && (
                      <div className="flex items-center gap-1 text-rose-600 text-xs font-medium">
                        <AlertTriangle size={12} /> Emergency
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{formatDoctorName(appt.doctor_name)} • {appt.visit_type} • {appt.complaint || "—"}</p>
                </div>

                {/* Status + Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`text-xs ${STATUS_STYLES[appt.status] || "bg-slate-100 text-slate-700"}`}>
                    {appt.status?.replace("_", " ")}
                  </Badge>
                  {STATUS_FLOW[appt.status] && (
                    <Button size="sm" className={`h-7 text-xs text-white ${STATUS_FLOW[appt.status].color}`} onClick={() => updateStatus(appt.id, STATUS_FLOW[appt.status].next)} data-testid={`reception-status-btn-${appt.id}`}>
                      {STATUS_FLOW[appt.status].label}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Book Appointment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">Book New Appointment</DialogTitle></DialogHeader>
          <form onSubmit={handleBook} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Patient</Label>
              <select value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="rec-patient-select">
                <option value="">Select Patient</option>
                {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.patient_id || p.phone})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Doctor</Label>
              <select value={form.doctor_id} onChange={e => setForm({...form, doctor_id: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="rec-doctor-select">
                <option value="">Select Doctor</option>
                {doctors.filter(d => d.is_available).map(d => <option key={d.id} value={d.id}>{formatDoctorName(d.name)} — {d.specialization}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Date & Time</Label>
              <Input type="datetime-local" value={form.visit_date} onChange={e => setForm({...form, visit_date: e.target.value})} className="mt-1 border-slate-200" required />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Complaint</Label>
              <Input value={form.complaint} onChange={e => setForm({...form, complaint: e.target.value})} placeholder="Chief complaint" className="mt-1 border-slate-200" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="rec-emergency" checked={form.is_emergency} onChange={e => setForm({...form, is_emergency: e.target.checked})} data-testid="rec-emergency-check" />
              <Label htmlFor="rec-emergency" className="text-sm text-rose-600 font-medium cursor-pointer">Emergency Case</Label>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-rec-appt-btn">
                {saving ? "Booking..." : "Book Appointment"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
